import React, { useState } from 'react';
import './ManualSleepEntry.css';
import { useToast } from '../contexts/ToastContext';

interface ManualSleepEntryProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (startTime: Date, endTime: Date) => void;
  babyId: string;
  use24h?: boolean;
}

const ManualSleepEntry: React.FC<ManualSleepEntryProps> = ({
  isOpen,
  onClose,
  onSubmit,
  babyId,
  use24h = false,
}) => {
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showToast } = useToast();

  // Initialize with current date/time when opened
  React.useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().slice(0, 5); // HH:MM

      setStartDate(dateStr);
      setStartTime(timeStr);
      setEndDate(dateStr);
      setEndTime(timeStr);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!startDate || !startTime || !endDate || !endTime) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    // Create Date objects
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    // Validation
    if (startDateTime >= endDateTime) {
      showToast('End time must be after start time', 'error');
      return;
    }

    const now = new Date();
    if (startDateTime > now) {
      showToast('Start time cannot be in the future', 'error');
      return;
    }

    if (endDateTime > now) {
      showToast('End time cannot be in the future', 'error');
      return;
    }

    // Check if sleep duration is reasonable (not more than 24 hours)
    const durationHours = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      showToast('Sleep duration cannot be more than 24 hours', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(startDateTime, endDateTime);
      onClose();
    } catch (err) {
      showToast('Failed to add sleep entry', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="manual-entry-modal">
        <div className="modal-header">
          <h3>Add Sleep Manually</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="manual-entry-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start-date">Start Date</label>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="start-time">Start Time</label>
              <input
                type="time"
                id="start-time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="end-date">End Date</label>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="end-time">End Time</label>
              <input
                type="time"
                id="end-time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-btn">
              {isSubmitting ? 'Adding...' : 'Add Sleep'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualSleepEntry;